import { useState } from "react";
import { Sparkle, X } from "@phosphor-icons/react";
import { useAction, useMutation } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { AdminAudienceState } from "../../shared/types.ts";
import type { RoleAuth } from "../lib/auth.ts";
import { notify } from "../lib/notify.ts";
import {
  audienceIdentityModeOptions,
  describeIdentityTransition,
  isAudienceIdentityModeSelectable,
} from "./audienceIdentityControls.ts";
import type { AudienceIdentityMode } from "../../shared/audienceIdentity.ts";
import { normalizeAudienceIdentityPolicy } from "../../shared/audienceIdentity.ts";

type SeedGenerationActionResult = {
  status: string;
  reason: string | null;
  generatedCount: number;
  storedCount: number;
  addedCount: number;
};

/**
 * Left-rail "Audience & categories" card. Replaced the V1 AudienceSetupPanel
 * (the join code moved to the topbar chip, so this card is policy toggles +
 * category taxonomy only) but kept its exact mutations and testids so the
 * e2e suite carried over unchanged.
 */
export function AudienceCategoriesCard({
  auth,
  sessionId,
  sessionTitle,
  generationPurpose,
  state,
}: {
  auth: RoleAuth;
  sessionId: string;
  sessionTitle: string;
  generationPurpose: string;
  state: AdminAudienceState;
}) {
  const setPolicy = useMutation(convexApi.settings.setAudiencePolicy);
  const beginFullAnonymityConversion = useMutation(convexApi.settings.beginFullAnonymityConversion);
  const retryFullAnonymityConversion = useMutation(convexApi.settings.retryFullAnonymityConversion);
  const createCategory = useMutation(convexApi.categories.createManual);
  const renameCategory = useMutation(convexApi.categories.rename);
  const recategorizeCategory = useMutation(convexApi.audience.recategorizeCategory);
  const deleteSeed = useMutation(convexApi.audienceCategorization.deleteSeed);
  const generateSeeds = useAction(convexApi.categoryCandidateActions.generateSeeds);
  const [catText, setCatText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [fullAnonymityConfirmationOpen, setFullAnonymityConfirmationOpen] = useState(false);
  const [fullAnonymityConfirmation, setFullAnonymityConfirmation] = useState("");
  const [isStartingFullAnonymity, setIsStartingFullAnonymity] = useState(false);

  // postVisibilityMode and replyVisibilityMode are always written together
  // by the toggle below, so checking both (not just replyVisibilityMode)
  // is what actually reflects the current auto-approve state.
  const autoApprove = state.policy.postVisibilityMode === "auto-shown"
    && state.policy.replyVisibilityMode === "auto-shown";
  const canGenerateSeeds = sessionTitle.trim().length > 0 || generationPurpose.trim().length > 0;
  const seedCandidates = state.categoryCandidates.filter((c) => c.source === "seed");
  const liveCandidates = state.categoryCandidates.filter((c) => c.source !== "seed");

  const generalCategory = state.categories.find((category) => category.slug === "general");
  const parkedCount = state.threads.filter((thread) => thread.aiCategoryStatus === "failed").length;
  const identityPolicy = normalizeAudienceIdentityPolicy(state.policy);

  async function changeIdentityMode(nextMode: AudienceIdentityMode) {
    if (nextMode === identityPolicy.identityMode) return;
    if (!isAudienceIdentityModeSelectable(identityPolicy.identityMode, nextMode)) return;
    const selected = audienceIdentityModeOptions.find((option) => option.value === nextMode);
    if (nextMode === "fully_anonymous") {
      setFullAnonymityConfirmation("");
      setFullAnonymityConfirmationOpen(true);
      return;
    }
    if (!window.confirm(`Change audience identity to “${selected?.label ?? nextMode}”?\n\nThis privacy increase changes how existing posts appear and cannot be reversed during this session.`)) return;
    try {
      await setPolicy({
        auth,
        sessionId: sessionId as Id<"sessions">,
        identityMode: nextMode,
      });
    } catch (error) {
      notify.error({
        title: "Could not change audience identity",
        description: error instanceof Error ? error.message : "The privacy setting was not changed.",
      });
    }
  }

  async function confirmFullAnonymity() {
    if (fullAnonymityConfirmation !== "ERASE NAMES" || isStartingFullAnonymity) return;
    setIsStartingFullAnonymity(true);
    try {
      await beginFullAnonymityConversion({
        auth,
        sessionId: sessionId as Id<"sessions">,
        confirmation: fullAnonymityConfirmation,
      });
      setFullAnonymityConfirmationOpen(false);
      notify.warning({
        title: "Full anonymity enabled",
        description: "Public responses now use random identities while stored names are erased in the background.",
      });
    } catch (error) {
      notify.error({
        title: "Could not start name erasure",
        description: error instanceof Error ? error.message : "The privacy setting was not changed.",
      });
    } finally {
      setIsStartingFullAnonymity(false);
    }
  }

  async function retryNameErasure() {
    try {
      await retryFullAnonymityConversion({ auth, sessionId: sessionId as Id<"sessions"> });
      notify.success({ title: "Name erasure resumed" });
    } catch (error) {
      notify.error({
        title: "Could not resume name erasure",
        description: error instanceof Error ? error.message : "Try again shortly.",
      });
    }
  }

  async function addCategory() {
    const label = catText.trim();
    if (!label) return;
    const result = await createCategory({ auth, sessionId: sessionId as Id<"sessions">, label });
    setCatText("");
    if (result.sweepCount > 0) {
      notify.success({
        title: "Category added",
        description: `Re-evaluating ${result.sweepCount} AI-categorized ${result.sweepCount === 1 ? "post" : "posts"}.`,
      });
    }
  }

  function startRename(id: string, label: string) {
    setEditingId(id);
    setEditText(label);
  }

  async function saveRename() {
    if (!editingId) return;
    const label = editText.trim();
    const current = state.categories.find((category) => category.id === editingId);
    if (!label || !current || label === current.label) {
      setEditingId(null);
      return;
    }
    try {
      await renameCategory({ auth, categoryId: editingId as Id<"audienceCategories">, label });
      setEditingId(null);
    } catch {
      notify.error({
        title: "Could not rename category",
        description: "A category with this name may already exist.",
      });
    }
  }

  async function rerunGeneral() {
    if (!generalCategory) return;
    try {
      const count = await recategorizeCategory({
        auth,
        sessionId: sessionId as Id<"sessions">,
        categoryId: generalCategory.id as Id<"audienceCategories">,
      });
      notify.success({
        title: "Re-running AI categorization",
        description: `${count} ${count === 1 ? "post" : "posts"} re-queued.`,
      });
    } catch {
      notify.error({ title: "Could not re-run AI categorization" });
    }
  }

  async function handleGenerateSeeds() {
    if (!canGenerateSeeds || isGenerating) return;
    setIsGenerating(true);
    try {
      const result: SeedGenerationActionResult = await generateSeeds({
        auth,
        sessionId: sessionId as Id<"sessions">,
      });
      notifySeedGenerationResult(result);
    } catch {
      notify.error({
        title: "Could not generate seed presets",
        description: "Check the AI provider configuration and try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <section className="panel admin2-categories" data-testid="admin2-categories">
      <div className="panel-head"><h2>Audience &amp; categories</h2></div>

      <div className="policy-row">
        <span>Posting open</span>
        <label className="switch">
          <input
            type="checkbox"
            data-testid="posting-open-switch"
            checked={state.policy.postingOpen}
            onChange={(e) => void setPolicy({
              auth,
              sessionId: sessionId as Id<"sessions">,
              postingOpen: e.target.checked,
            })}
          />
        </label>
      </div>
      <div className="policy-row">
        <span>Auto-approve posts &amp; replies</span>
        <label className="switch">
          <input
            type="checkbox"
            data-testid="auto-approve-switch"
            checked={autoApprove}
            onChange={(e) => void setPolicy({
              auth,
              sessionId: sessionId as Id<"sessions">,
              postVisibilityMode: e.target.checked ? "auto-shown" : "queued",
              replyVisibilityMode: e.target.checked ? "auto-shown" : "queued",
            })}
          />
        </label>
      </div>
      <div className="audience-identity-settings" data-testid="audience-identity-settings">
        <div className="tax-label">Participant identity</div>
        <div className="audience-identity-options" role="radiogroup" aria-label="Participant identity mode">
          {audienceIdentityModeOptions.map((option) => {
            const selectable = state.identityTransition?.status !== "erasing" &&
              isAudienceIdentityModeSelectable(identityPolicy.identityMode, option.value);
            return (
              <label
                className={`audience-identity-option ${option.value === identityPolicy.identityMode ? "is-selected" : ""} ${selectable ? "" : "is-disabled"}`}
                key={option.value}
                title={selectable ? option.description : "A live session cannot be changed to a less-private identity mode."}
              >
                <input
                  type="radio"
                  name="audience-identity-mode"
                  value={option.value}
                  checked={option.value === identityPolicy.identityMode}
                  disabled={!selectable}
                  onChange={() => void changeIdentityMode(option.value)}
                />
                <span><strong>{option.label}</strong><small>{option.description}</small></span>
              </label>
            );
          })}
        </div>
        {state.identityTransition && (() => {
          const transition = describeIdentityTransition(state.identityTransition);
          return (
            <div
              className={`identity-transition-status is-${state.identityTransition.status}`}
              data-testid="identity-transition-status"
              aria-live="polite"
            >
              <strong>{transition.label}</strong>
              <span>{transition.detail}</span>
              {transition.retryable && (
                <button type="button" className="btn ghost sm" onClick={() => void retryNameErasure()}>
                  Retry erasure
                </button>
              )}
            </div>
          );
        })()}
      </div>
      <div className="policy-row policy-row-public-authors">
        <span>
          Show author labels publicly
          <span className="dnote">Participant and projector screens always match.</span>
        </span>
        <label className="switch">
          <input
            type="checkbox"
            data-testid="public-author-display-switch"
            checked={identityPolicy.publicAuthorDisplay === "identity"}
            onChange={(e) => void setPolicy({
              auth,
              sessionId: sessionId as Id<"sessions">,
              publicAuthorDisplay: e.target.checked ? "identity" : "hidden",
            })}
          />
        </label>
      </div>

      <div className="tax-group">
        <div className="tax-label">Active categories · {state.categories.length}</div>
        <div className="tax-chips">
          {state.categories.map((category) => (
            editingId === category.id ? (
              <input
                key={category.id}
                data-testid={`category-rename-input-${category.slug}`}
                value={editText}
                size={Math.max(editText.length, 6)}
                autoFocus
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveRename();
                  if (e.key === "Escape") setEditingId(null);
                }}
                onBlur={() => setEditingId(null)}
              />
            ) : (
              <span
                className="tax-chip active"
                key={category.id}
                role="button"
                tabIndex={0}
                title="Click to rename"
                data-testid={`category-chip-${category.slug}`}
                onClick={() => startRename(category.id, category.label)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    startRename(category.id, category.label);
                  }
                }}
              >{category.label}</span>
            )
          ))}
        </div>
        {parkedCount > 0 && generalCategory && (
          <div className="dnote" data-testid="general-fallback-nudge">
            {parkedCount} auto-filed to General after AI errors ·{" "}
            <button type="button" className="expand" onClick={() => void rerunGeneral()}>
              Re-run AI
            </button>
          </div>
        )}
      </div>

      <div className="tax-group">
        <div className="tax-label">Presets — not yet used</div>
        <div className="tax-chips">
          {seedCandidates.map((candidate) => (
            <span className="tax-chip seed" key={candidate.id} title={candidate.description ?? candidate.label}>
              {candidate.label}
              <span className="src-b">seed</span>
              <button
                type="button"
                className="x"
                aria-label={`Delete seed ${candidate.label}`}
                data-testid={"delete-seed-" + candidate.slug}
                onClick={() => void deleteSeed({
                  auth,
                  sessionId: sessionId as Id<"sessions">,
                  candidateId: candidate.id as Id<"audienceCategoryCandidates">,
                })}
              >
                <X weight="bold" />
              </button>
            </span>
          ))}
          {liveCandidates.map((candidate) => (
            <span className="tax-chip live" key={candidate.id} title={candidate.description ?? candidate.label}>
              {candidate.label}
              <span className="src-b">live</span>
            </span>
          ))}
        </div>
      </div>

      <div className="tax-add">
        <input
          placeholder="Add a category…"
          data-testid="category-add-input"
          value={catText}
          onChange={(e) => setCatText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void addCategory(); }}
        />
        <button
          type="button"
          className="btn ghost sm"
          data-testid="generate-category-seeds-button"
          disabled={!canGenerateSeeds || isGenerating}
          onClick={() => void handleGenerateSeeds()}
          title={canGenerateSeeds ? "Generate seed topic candidates from session setup." : "Add a session title or purpose first."}
        >
          <Sparkle weight="fill" />
          {isGenerating ? " Generating..." : " Seeds"}
        </button>
      </div>

      {fullAnonymityConfirmationOpen && (
        <div
          className="confirm-scrim"
          data-testid="full-anonymity-confirmation"
          onClick={() => { if (!isStartingFullAnonymity) setFullAnonymityConfirmationOpen(false); }}
        >
          <div
            className="confirm-panel identity-erasure-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="identity-erasure-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="identity-erasure-title">Permanently erase participant names?</h3>
            <p className="confirm-body">
              Public responses will become fully anonymous immediately. Every stored participant name in this session
              will then be permanently erased in the background. This cannot be undone.
            </p>
            <label className="identity-erasure-confirm-label">
              Type <strong>ERASE NAMES</strong> to continue
              <input
                autoFocus
                value={fullAnonymityConfirmation}
                disabled={isStartingFullAnonymity}
                aria-label="Full anonymity confirmation text"
                onChange={(event) => setFullAnonymityConfirmation(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void confirmFullAnonymity();
                  if (event.key === "Escape" && !isStartingFullAnonymity) setFullAnonymityConfirmationOpen(false);
                }}
              />
            </label>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn ghost"
                disabled={isStartingFullAnonymity}
                onClick={() => setFullAnonymityConfirmationOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={fullAnonymityConfirmation !== "ERASE NAMES" || isStartingFullAnonymity}
                onClick={() => void confirmFullAnonymity()}
              >
                {isStartingFullAnonymity ? "Starting…" : "Erase names permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function notifySeedGenerationResult(result: SeedGenerationActionResult) {
  if (result.status === "success") {
    notify.success({
      title: "Seed presets ready",
      description: `${result.addedCount} new ${pluralizePreset(result.addedCount)} added. ${result.storedCount} ${pluralizePreset(result.storedCount)} available.`,
    });
    return;
  }

  if (result.status === "no_new_seeds") {
    notify.warning({
      title: "No new seed presets",
      description: result.generatedCount > 0
        ? "The generated topics were already present or already active."
        : "The provider did not return usable seed topics.",
    });
    return;
  }

  notify.error({
    title: "Could not generate seed presets",
    description: seedGenerationFailureDescription(result.reason),
  });
}

function seedGenerationFailureDescription(reason: string | null): string {
  if (reason === "missing_api_key") return "GROQ_API_KEY is not configured in Convex.";
  if (reason === "provider_error") return "The AI provider rejected or failed the request.";
  return "The provider did not return usable seed topics.";
}

function pluralizePreset(count: number): string {
  return count === 1 ? "preset" : "presets";
}
